import type { EsProductFragment, QuickSearchQuery } from '#graphql-operations'

export function useQuickSearch() {
  const data = useState<QuickSearchQuery>('quickSearchData', () => null)
  const loading = useState('quickSearchLoading', () => false)
  const error = useState('quickSearchError', () => null)

  const search = useState('search', () => '')
  const searchDialogOpen = useState('searchDialogOpen', () => false)

  const currentFocusIndex = useState('currentFocusIndex', () => -1)

  const searchElement = useState<HTMLElement | null>('searchElement', () => null)
  const searchResults = useState<HTMLElement | null>('searchResults', () => null)
  const searchInput = useState<HTMLInputElement | null>('searchInput', () => null)

  const recentSearches = useLocalStorage<string[]>('recentSearches', [], { initOnMounted: true })

  const setSearch = (newSearch: string) => {
    search.value = newSearch
    searchInput.value?.focus()
    currentFocusIndex.value = -1
  }

  const openDialog = () => {
    searchDialogOpen.value = true
    search.value = ''
    currentFocusIndex.value = -1

    nextTick(() => {
      searchInput.value?.focus()
    })
  }

  const closeDialog = () => {
    searchDialogOpen.value = false
    search.value = ''
    currentFocusIndex.value = -1

    nextTick(() => {
      searchInput.value?.blur()
    })
  }

  const removeFocusVisible = (event: FocusEvent) => {
    (event.target as HTMLElement).classList.remove(':focus-visible')
  }

  const onKeyPressElement = (event: KeyboardEvent) => {
    if (!searchDialogOpen.value) return

    const resultElements = getInteractiveElements(searchResults.value)

    let focusedElement = null

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (currentFocusIndex.value > 0) {
        currentFocusIndex.value--
      }
      else {
        currentFocusIndex.value = resultElements.length - 1
      }
      focusedElement = resultElements[currentFocusIndex.value]
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (currentFocusIndex.value < resultElements.length - 1) {
        currentFocusIndex.value++
      }
      else {
        currentFocusIndex.value = 0
      }
      focusedElement = resultElements[currentFocusIndex.value]
    }

    if (focusedElement && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
      focusedElement.addEventListener('focusout', removeFocusVisible, { once: true })
      focusedElement.classList.add(':focus-visible')
      focusedElement.focus()
    }
  }

  const onKeyPressWindow = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeDialog()
    }
  }

  const doSearch = () => {
    setTimeout(() => {
      searchRedirect()
      closeDialog()
      searchInput.value?.blur()
    }, 600)
  }

  const highlight = (label: string) => {
    const pattern = new RegExp(search.value, 'gi')
    return label.replaceAll(pattern, `<mark class="search__highlight">${search.value}</mark>`)
  }

  function getInteractiveElements(container: HTMLElement | null) {
    return Array.from((container || document).querySelectorAll('a[href], button:enabled, input:enabled:not([type="hidden"]), textarea:enabled, select:enabled, label, area[href], object, iframe, [tabindex]:not([tabindex^="-"]), [contenteditable="true"]')) as HTMLElement[]
  }

  const setFocusOnFirstItem = () => {
    const focusableElements = getInteractiveElements(searchElement.value)
    const resultElements = focusableElements.slice(2, -1)
    if (resultElements.length > 0) {
      currentFocusIndex.value = 0
      resultElements[0]?.focus()
    }
  }

  const setFocusOnLastItem = () => {
    const focusableElements = getInteractiveElements(searchElement.value)
    const resultElements = focusableElements.slice(2, -1)
    if (resultElements.length > 0) {
      currentFocusIndex.value = resultElements.length - 1
      resultElements[resultElements.length - 1]?.focus()
    }
  }

  const variables = computed(() => ({
    search: search.value,
  }))

  const enabled = computed(() => search.value.length > 2)

  const searchRedirect = () => {
    if (!search.value) {
      return
    }
    const router = useRouter()

    const matching = [...categories.value, ...pages.value]
      .find(({ label }: { label: string }) => label.toLowerCase() === search.value.toLowerCase())
    if (matching) {
      router.push({ path: '/' + matching.url + '/' })
    }
    else {
      router.push({ path: '/search/', query: { q: search.value } })
    }
  }

  const products = computed<EsProductFragment[]>(() => data.value?.products.products)
  const totalProducts = computed(() => data.value?.products.totalCount || 0)
  const categories = computed(() => data.value?.categories.urls || [])
  const pages = computed(() => data.value?.pages.urls || [])
  const suggestions = computed(() => {
    // const combined = [
    //   ...(data.value?.routeSuggestions?.suggestions || []),
    //   ...(data.value?.productSuggestions?.suggestions || []),
    // ]
    //   .toSorted((a, b) => b.score - a.score)
    //   .map(suggestion => suggestion.text)
    //   .filter(suggestion => suggestion.toLowerCase() !== search.value.toLowerCase())

    // const seen = new Set()
    // const unique = combined.filter((item) => {
    //   const lower = item.toLowerCase()
    //   if (seen.has(lower)) {
    //     return false
    //   }
    //   seen.add(lower)
    //   return true
    // })

    // return unique
    return []
  })

  const hasResults = computed(() => {
    return totalProducts.value > 0 || categories.value.length > 0 || pages.value.length > 0
  })

  watch(data, () => {
    if (hasResults.value) {
      const newRecentSearches = new Set([search.value, ...recentSearches.value])
      recentSearches.value = Array.from(newRecentSearches).slice(0, 5)
    }
  })

  watchDebounced(search, () => {
    if (!enabled.value) {
      data.value = null
      loading.value = false
      return
    }

    loading.value = true
    error.value = undefined

    useGraphqlQuery('quickSearch', variables.value, useGraphqlDefaultClient())
      .then((response) => {
        if (response.error) {
          error.value = response.error
          data.value = null
        }
        else {
          data.value = response.data
        }
      })
      .catch((err) => {
        error.value = err
        data.value = null
      })
      .finally(() => {
        loading.value = false
      })
  },
  { debounce: 500, maxWait: 1500 },
  )

  return {
    data,
    search,
    searchDialogOpen,
    recentSearches,
    suggestions,

    error,
    loading,

    hasResults,
    categories,
    pages,
    products,
    totalProducts,

    searchRedirect,

    //

    searchElement,
    searchResults,
    searchInput,

    openDialog,
    closeDialog,
    setSearch,
    onKeyPressElement,
    onKeyPressWindow,
    doSearch,
    highlight,
    setFocusOnFirstItem,
    setFocusOnLastItem,
    currentFocusIndex,
  }
}
